import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Grid,
  TextField,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { tacticalAccordionSx, tacticalSummarySx, tacticalChipSx } from "../../utils/fdcTheme";

const compactFieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.25, height: 44 },
  "& .MuiInputLabel-root": { fontSize: 13, fontWeight: 700 },
  "& input": { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
};

// MET + VE correction inputs. All optional — blank fields fall back to standard
// conditions, so the firing solution equals the base table lookup. Applied by the
// MT4-306 solver for M109 HE / SMK / ILLUM.
const MATERIEL_FIELDS = [
  { name: "mv_ve", label: "Velocity Error (m/s)", help: "Muzzle-velocity variation from the registration" },
  { name: "propellant_temp_c", label: "Propellant Temp (°C)", help: "Standard 21 °C" },
  { name: "proj_squares", label: "Proj. Weight (squares)", help: "Standard 4" },
];
const MET_FIELDS = [
  { name: "wind_dir_mil", label: "Wind From (mils)", help: "Grid azimuth the wind blows FROM" },
  { name: "wind_speed_kn", label: "Wind Speed (kn)" },
  { name: "air_temp_pct", label: "Air Temp (% std)", help: "Standard 100 %" },
  { name: "air_density_pct", label: "Air Density (% std)", help: "Standard 100 %" },
];

export default function MetVePanel({ expanded, onChange, hasMission, form, handleChange }) {
  const activeCount = [...MATERIEL_FIELDS, ...MET_FIELDS].filter(
    (f) => String(form[f.name] ?? "").trim() !== "",
  ).length;

  const renderField = (f) => (
    <Grid item xs={6} md={3} key={f.name}>
      <TextField
        fullWidth
        size="small"
        type="number"
        label={f.label}
        name={f.name}
        value={form[f.name] ?? ""}
        onChange={handleChange}
        placeholder="std"
        helperText={f.help}
        sx={{ ...compactFieldSx, "& .MuiFormHelperText-root": { fontSize: 10.5, margin: "2px 0 0 0" } }}
      />
    </Grid>
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disabled={!hasMission}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={(theme) => tacticalSummarySx(theme)}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>MET / VE Corrections</Typography>
          {activeCount > 0 ? (
            <Chip size="small" label={`${activeCount} active`} sx={(theme) => tacticalChipSx(theme, "warning")} />
          ) : (
            <Chip size="small" label="standard" sx={(theme) => tacticalChipSx(theme)} />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.5 }, pb: 1.5, pt: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
          Leave blank for standard conditions. Applied by the firing-table solver (M109 HE / Smoke / Illum).
          High/Low angle is set on the fire bar.
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.4 }}>
            VELOCITY &amp; MATERIEL
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.1 }}>
            {MATERIEL_FIELDS.map(renderField)}
          </Grid>
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.4 }}>
            METEO (BALLISTIC)
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.1 }}>
            {MET_FIELDS.map(renderField)}
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
